/**
 * @file registerUi.tsx
 * @brief The pieces both sign-up pages share: header, section heading, the
 *        bare picker field, the password step and the post-signup questions.
 *
 * @details
 * Ports of the phone's Register screens (FRONTEND/src/screens/Auth): same
 * titles, sections, field order and wording, in the web's bare field style
 * (`FormFieldGroup` with icons, as the web Login page).
 *
 * @author DES
 * @date 01-10-2026
 */

import { useMemo, useState, type ReactNode } from "react";
import { useForm, useWatch, type UseFormRegisterReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  CircleCheck,
  Circle,
  Eye,
  EyeOff,
  Languages,
  LoaderCircle,
  Lock,
  Type,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { TitleBadge } from "@/components/ui/TitleBadge";
import { apiGet } from "@/shared/api/client";
import { scheduleEndpoints } from "@/shared/api/endpoints";
import { FALLBACKS, safeHttps } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";
import { TITLES, type TitleOption } from "@/shared/data/titles";
import { cn } from "@/shared/theme/cn";
import {
  bareCaptionClass,
  bareCaptionErrorClass,
  fieldErrorText,
} from "@/components/ui/fieldStyle";
import { submitOnboarding } from "./registerApi";
import {
  PASSWORD_RULES,
  PRIMARY_USES,
  SIGNUP_SOURCES,
  SIGNUP_SOURCE_DETAIL_CODE,
  SIGNUP_SOURCE_DETAIL_MAX,
  passwordFormSchema,
  type PasswordFormValues,
} from "./registerShared";

// ----------------------------------------------------------------------
// Header: round back button, 28px bold title, one-line purpose
// ----------------------------------------------------------------------
export function RegisterHeader({
  title,
  subtitle,
  onBack,
}: {
  title: string;
  subtitle?: ReactNode;
  onBack: () => void;
}) {
  return (
    <div className="mb-8 flex items-start gap-3 [@media(max-height:720px)]:mb-4">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-app-quaternary text-foreground transition-colors hover:bg-app-quaternary-dark"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>
      <div className="min-w-0 flex-1 pt-[5px]">
        <h2 className="text-[28px] font-bold leading-[34px] tracking-tight text-foreground">
          {title}
        </h2>
        {subtitle ? (
          <p className="mt-1 whitespace-pre-line text-[15px] leading-5 text-muted-foreground [@media(max-height:720px)]:hidden">
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Section heading with the phone's "card done" tick
// ----------------------------------------------------------------------
export function RegisterSection({
  label,
  done,
  children,
}: {
  label: string;
  done: boolean;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3.5 [@media(max-height:720px)]:space-y-2">
      <h3 className="flex items-center gap-1.5 text-[15px] font-bold tracking-[-0.2px] text-app-text/50">
        {label}
        {done ? <CircleCheck className="h-4 w-4 text-emerald-500" aria-label="Done" /> : null}
      </h3>
      {children}
    </section>
  );
}

// ----------------------------------------------------------------------
// Bare picker field: caption + brand icon + value + chevron, opening a
// menu under it (PickerSheet `anchor`, the web picker standard)
// ----------------------------------------------------------------------
function BarePickerField<T extends PickerSheetItem>({
  label,
  icon,
  valueLabel,
  leading,
  error,
  hint,
  items,
  selectedId,
  onSelect,
  sheetTitle,
  searchPlaceholder,
  loading,
  renderLeftAccessory,
}: {
  label: string;
  icon: ReactNode;
  valueLabel: string | null;
  leading?: ReactNode;
  error?: string;
  hint?: string;
  items: T[];
  selectedId: string | null;
  onSelect: (item: T) => void;
  sheetTitle: string;
  searchPlaceholder: string;
  loading?: boolean;
  renderLeftAccessory?: (item: T) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // The phone's bare validation: the error takes the label's place in red.
  const errorText = error ? fieldErrorText(label, error) : null;
  return (
    <div className="relative pb-2.5">
      <span
        className={cn(
          bareCaptionClass,
          "truncate pl-8",
          errorText && bareCaptionErrorClass,
          !valueLabel && "invisible",
        )}
      >
        {errorText ?? label}
      </span>
      <PickerSheet<T>
        anchor={
          <button
            type="button"
            aria-label={label}
            className="flex min-h-6 w-full items-center gap-3 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex w-5 shrink-0 items-center justify-center text-brand [&_svg]:h-5 [&_svg]:w-5">
              {icon}
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2">
              {valueLabel ? leading : null}
              <span
                className={cn(
                  "truncate text-[17px] font-medium leading-[21px]",
                  valueLabel
                    ? "text-app-text"
                    : errorText
                      ? "text-destructive"
                      : "text-muted-foreground/70",
                )}
              >
                {valueLabel ?? errorText ?? label}
              </span>
            </span>
            <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        title={sheetTitle}
        searchPlaceholder={searchPlaceholder}
        items={items}
        selectedId={selectedId}
        onSelect={(item) => {
          onSelect(item);
          setOpen(false);
        }}
        loading={loading}
        renderLeftAccessory={renderLeftAccessory}
      />
      {!errorText && hint ? (
        <p className="mt-1 pl-8 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      <span className="absolute bottom-0 left-8 right-0 h-px bg-app-text/10" aria-hidden />
    </div>
  );
}

interface TitleItem extends PickerSheetItem {
  title: TitleOption;
}

/** Title, stored as its code ("mr"), shown as its label ("Mr."). */
export function TitleField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (code: string) => void;
  error?: string;
}) {
  const items = useMemo<TitleItem[]>(
    () => TITLES.map((t) => ({ id: t.code, label: t.label, searchKeys: [t.code], title: t })),
    [],
  );
  const matched = TITLES.find((t) => t.code === value);
  return (
    <BarePickerField<TitleItem>
      label="Title"
      icon={<Type />}
      valueLabel={matched?.label ?? null}
      leading={matched ? <TitleBadge title={matched} size="sm" /> : null}
      error={error}
      items={items}
      selectedId={matched?.code ?? null}
      onSelect={(item) => onChange(item.title.code)}
      sheetTitle={`Select title (${TITLES.length})`}
      searchPlaceholder="Search titles…"
      renderLeftAccessory={(item) => <TitleBadge title={item.title} size="lg" />}
    />
  );
}

const LanguagesResponseSchema = z.object({
  languages: z.array(
    z.object({ code: z.string(), label: z.string(), locale: z.string().optional() }),
  ),
});
type Lang = z.infer<typeof LanguagesResponseSchema>["languages"][number];
interface LangItem extends PickerSheetItem {
  language: Lang;
}

/**
 * Native language, stored as the locale when there is one ("en-GB") so
 * regional variants stay distinct, as the phone's `languageIdentifier`.
 * Same public list and query key as `LanguagePicker`.
 */
export function NativeLanguageField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (id: string) => void;
  error?: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["s2s", "languages"],
    queryFn: async () =>
      LanguagesResponseSchema.parse(await apiGet<unknown>(scheduleEndpoints.languages)).languages,
    staleTime: 5 * 60_000,
  });
  const items = useMemo<LangItem[]>(
    () =>
      (data ?? []).map((l) => ({
        id: l.locale || l.code,
        label: l.label,
        subtitle: l.locale ?? l.code,
        searchKeys: [l.code, l.locale ?? ""],
        language: l,
      })),
    [data],
  );
  const matched = items.find((i) => i.id === value);
  return (
    <BarePickerField<LangItem>
      label="Native Language"
      icon={<Languages />}
      valueLabel={matched?.label ?? null}
      leading={matched ? <LanguageFlag language={matched.language} size="sm" /> : null}
      error={error}
      hint="Used so Meeting Intel can show minutes in your language. You can change it later in Edit Profile."
      items={items}
      selectedId={matched?.id ?? null}
      onSelect={(item) => onChange(item.id)}
      sheetTitle="Select language"
      searchPlaceholder="Search languages…"
      loading={isLoading}
      renderLeftAccessory={(item) => <LanguageFlag language={item.language} size="lg" />}
    />
  );
}

// ----------------------------------------------------------------------
// "Already have an account? Sign in"
// ----------------------------------------------------------------------
export function SignInLink({ onClick }: { onClick: () => void }) {
  return (
    <p className="mt-3 text-center text-sm text-muted-foreground">
      Already have an account?{" "}
      <button type="button" onClick={onClick} className="font-semibold text-brand hover:underline">
        Sign in
      </button>
    </p>
  );
}

export function ServerError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive"
    >
      {message}
    </div>
  );
}

// ----------------------------------------------------------------------
// Password step (RegisterPassword / RegisterBusinessPassword)
// ----------------------------------------------------------------------
function PasswordBox({
  id,
  label,
  error,
  autoFocus,
  registerProps,
}: {
  id: string;
  label: string;
  error?: string;
  autoFocus?: boolean;
  registerProps: UseFormRegisterReturn;
}) {
  const [show, setShow] = useState(false);
  return (
    <FormFieldGroup label={label} htmlFor={id} icon={<Lock />} error={error}>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          autoFocus={autoFocus}
          className="flex-1"
          aria-invalid={error ? "true" : "false"}
          {...registerProps}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </FormFieldGroup>
  );
}

export function PasswordStep({
  title,
  subtitle,
  onBack,
  onCreate,
  serverError,
  passwordLabel = "Password",
  submitLabel = "Create Account",
  busyLabel = "Creating account…",
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
  /** Resolves when the account exists; throws (shown by the caller) if not. */
  onCreate: (password: string) => Promise<void>;
  serverError: string | null;
  /** Reset password says "New Password" / "Reset Password" (01-10-2026). */
  passwordLabel?: string;
  submitLabel?: string;
  busyLabel?: string;
}) {
  // Terms + Privacy from Links & company info (B23), else the old links.
  const links = usePublicConfig();
  const termsUrl = safeHttps(links.value("TERMS_URL", FALLBACKS.TERMS_URL), FALLBACKS.TERMS_URL);
  const privacyUrl = safeHttps(links.value("PRIVACY_URL", FALLBACKS.PRIVACY_URL), FALLBACKS.PRIVACY_URL);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { password: "", confirm: "" },
  });
  const password = useWatch({ control, name: "password" }) ?? "";

  return (
    <div>
      <RegisterHeader title={title} subtitle={subtitle} onBack={onBack} />
      <form onSubmit={handleSubmit((v) => onCreate(v.password))} className="space-y-3.5" noValidate>
        <PasswordBox
          id="password"
          label={passwordLabel}
          autoFocus
          error={errors.password?.message}
          registerProps={register("password")}
        />
        {/* The live checklist, as the phone's PasswordRequirementsField. */}
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 pl-8 text-xs">
          {PASSWORD_RULES.map((rule) => {
            const met = password.length > 0 && rule.test(password);
            return (
              <li
                key={rule.message}
                className={cn(
                  "flex items-center gap-1.5",
                  met ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
                )}
              >
                {met ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                {rule.message}
              </li>
            );
          })}
        </ul>
        <PasswordBox
          id="confirm"
          label="Confirm Password"
          error={errors.confirm?.message}
          registerProps={register("confirm")}
        />
        <ServerError message={serverError} />
        {/* GDPR (06-10-2026): this line is the agreement; the page records it
            with the new account's token (acceptCurrentTerms). */}
        <p className="text-center text-xs text-muted-foreground">
          By selecting {submitLabel} you agree to the{" "}
          <a href={termsUrl} target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href={privacyUrl} target="_blank" rel="noreferrer" className="font-medium text-brand hover:underline">
            Privacy Policy
          </a>
          .
        </p>
        <Button
          type="submit"
          className="mt-1 h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90"
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="h-4 w-4 animate-spin" />
              {busyLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------------
// Post-signup questions (SignupSourceScreen, PrimaryUseScreen)
// The phone's mic and notification steps are app-only and not asked here.
// ----------------------------------------------------------------------
function OptionList({
  options,
  selected,
  onPick,
}: {
  options: readonly { code: string; label: string }[];
  selected: string | null;
  onPick: (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = o.code === selected;
        return (
          <button
            key={o.code}
            type="button"
            onClick={() => onPick(o.code)}
            aria-pressed={on}
            className={cn(
              "rounded-full border px-4 py-2 text-sm font-medium transition-colors",
              on
                ? "border-brand bg-brand text-brand-foreground"
                : "border-input bg-background text-foreground hover:bg-accent",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function SignupQuestions({ token, onDone }: { token: string; onDone: () => void }) {
  const [step, setStep] = useState<"source" | "use">("source");
  const [source, setSource] = useState<string | null>(null);
  const [detail, setDetail] = useState("");
  const [use, setUse] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = async () => {
    setBusy(true);
    if (step === "source") {
      if (source) {
        await submitOnboarding(token, {
          source,
          ...(source === SIGNUP_SOURCE_DETAIL_CODE && detail.trim()
            ? { detail: detail.trim() }
            : {}),
        });
      }
      setBusy(false);
      setStep("use");
      return;
    }
    if (use) await submitOnboarding(token, { primaryUse: use });
    setBusy(false);
    onDone();
  };
  const skip = () => (step === "source" ? setStep("use") : onDone());

  const isSource = step === "source";
  const picked = isSource ? source : use;

  return (
    <div>
      <div className="mb-8">
        <h2 className="whitespace-pre-line text-[28px] font-bold leading-[34px] tracking-tight text-foreground">
          {isSource
            ? "Where did you hear\nabout Verblizr?"
            : "Pick the best fit for\nyour daily work."}
        </h2>
        <p className="mt-1 whitespace-pre-line text-[15px] leading-5 text-muted-foreground">
          {isSource
            ? "One tap helps us know where to show up.\nYou can skip this."
            : "Your Home slide button starts it.\nYou can change it later in Profile."}
        </p>
      </div>
      <OptionList
        options={isSource ? SIGNUP_SOURCES : PRIMARY_USES}
        selected={picked}
        onPick={isSource ? setSource : setUse}
      />
      {isSource && source === SIGNUP_SOURCE_DETAIL_CODE ? (
        <div className="mt-5">
          <FormFieldGroup label="Where exactly?" htmlFor="signup-detail">
            <Input
              id="signup-detail"
              value={detail}
              maxLength={SIGNUP_SOURCE_DETAIL_MAX}
              onChange={(e) => setDetail(e.target.value)}
            />
          </FormFieldGroup>
        </div>
      ) : null}
      <div className="mt-8 space-y-2">
        <Button
          type="button"
          className="h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90"
          disabled={!picked || busy}
          onClick={next}
          size="lg"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Next"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-11 w-full text-[15px] font-semibold text-muted-foreground"
          disabled={busy}
          onClick={skip}
        >
          Skip
        </Button>
      </div>
    </div>
  );
}
